import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import type { z } from "zod";

import {
  COOKIE_NAMES,
  clearAnonUserIdCookie,
  clearRefCodeCookie,
  parseAnonUserIdCookie,
} from "~/lib/cookies";
import { UserSignupSchema } from "~/lib/schemas/user-auth";
import { isRecentClerkSignup } from "~/lib/shared/recent-signup";
import { parseReferralCodeCookie } from "~/lib/shared/referral";
import { getCookieValue } from "~/lib/utils";

import type { UserInsert } from "../../../drizzle/schema";

type SignupData = z.infer<typeof UserSignupSchema>;

type SignupInsert = Omit<UserInsert, "id">;

const transformSignupDataToUserInsert = (
  signupData: SignupData,
  clerkUserId: string,
): SignupInsert => ({
  firstName: signupData.firstName,
  lastName: signupData.lastName,
  email: signupData.email,
  phoneNumber: signupData.phoneNumber ?? null,
  clerkUserId,
  profilePictureUrl: null,
});

const normalizeEmail = (value: string): string => value.trim().toLowerCase();

export const saveUserSignup = createServerFn({ method: "POST" })
  .validator((data: unknown) => UserSignupSchema.parse(data))
  .handler(async ({ data }) => {
    const [
      { getSessionClerkUserId },
      { initializeClerkIfNeeded, getClerk },
      { getDBWithInit },
      { users },
      { eq },
      { isAnonymousUser },
    ] = await Promise.all([
      import("~/lib/server/auth-helpers"),
      import("~/lib/server/clerk-server"),
      import("~/lib/db"),
      import("../../../drizzle/schema"),
      import("drizzle-orm"),
      import("~/lib/server/user"),
    ]);

    // Every exported server fn is reachable over HTTP, so the account comes
    // from the caller's session. Taken from the input, it would let anyone who
    // knows a new user's Clerk id and email write to that user's row.
    const clerkUserId = await getSessionClerkUserId();
    if (!clerkUserId) {
      throw new Error("Authentication required");
    }

    await initializeClerkIfNeeded();
    const clerkClient = getClerk();
    if (!clerkClient) {
      throw new Error("Clerk client not available");
    }

    const clerkUser = await clerkClient.users.getUser(clerkUserId).catch(() => {
      throw new Error("Invalid user - not found in Clerk");
    });

    const clerkEmails = clerkUser.emailAddresses.map((item) =>
      normalizeEmail(item.emailAddress),
    );
    if (!clerkEmails.includes(data.email)) {
      throw new Error("Authentication error - email verification failed");
    }

    if (!isRecentClerkSignup(clerkUser.createdAt)) {
      throw new Error("Authentication error - user verification expired");
    }

    const transform = transformSignupDataToUserInsert(data, clerkUserId);

    const db = await getDBWithInit();

    // Check for anonymous user from discovery flow
    const cookieHeader = getRequestHeader("cookie") || "";
    const anonUserIdCookie = getCookieValue(
      cookieHeader,
      COOKIE_NAMES.anonUserId,
    );
    const anonUserId = parseAnonUserIdCookie(anonUserIdCookie ?? undefined);

    // Resolve a captured referral code (set site-wide when the visitor arrived
    // via a `?ref=` link) to the referrer's user id. Generate a referral code
    // for this user so they can refer others in turn.
    const refCode = parseReferralCodeCookie(
      getCookieValue(cookieHeader, COOKIE_NAMES.refCode),
    );
    const { resolveReferrerUserId, generateUniqueUserReferralCode } =
      await import("~/lib/server/referral");
    const referrerUserId = refCode
      ? await resolveReferrerUserId(db, refCode)
      : null;
    const candidateReferralCode = await generateUniqueUserReferralCode(db);

    // For an existing user record, only fill referral fields that aren't set yet
    // (never overwrite an established code or an earlier attribution).
    const buildReferralFields = (existing: {
      id: string;
      referralCode: string | null;
      referredByUserId: string | null;
    }): Partial<UserInsert> => {
      const fields: Partial<UserInsert> = {};
      if (!existing.referralCode) {
        fields.referralCode = candidateReferralCode;
      }
      if (
        !existing.referredByUserId &&
        referrerUserId &&
        referrerUserId !== existing.id
      ) {
        fields.referredByUserId = referrerUserId;
      }
      return fields;
    };

    await db.transaction(async (tx) => {
      // Check if user already exists with this Clerk ID
      const [existingClerkUser] = await tx
        .select()
        .from(users)
        .where(eq(users.clerkUserId, clerkUserId))
        .for("update")
        .limit(1);

      if (existingClerkUser) {
        // User already linked to Clerk - just update their info
        await tx
          .update(users)
          .set({
            ...transform,
            ...buildReferralFields(existingClerkUser),
          })
          .where(eq(users.id, existingClerkUser.id));
        return;
      }

      // Check if there's an anonymous user from the discovery flow to link
      if (anonUserId) {
        const [anonUser] = await tx
          .select()
          .from(users)
          .where(eq(users.id, anonUserId))
          .for("update")
          .limit(1);

        if (anonUser && isAnonymousUser(anonUser)) {
          // Link the anonymous user to Clerk (preserves their preferences)
          await tx
            .update(users)
            .set({ ...transform, ...buildReferralFields(anonUser) })
            .where(eq(users.id, anonUserId));
          return;
        }
      }

      // No existing user to link - create new user
      const newUserId = crypto.randomUUID();
      await tx.insert(users).values({
        ...transform,
        id: newUserId,
        referralCode: candidateReferralCode,
        referredByUserId:
          referrerUserId && referrerUserId !== newUserId
            ? referrerUserId
            : null,
      });
    });

    // Clear the anonymous user cookie now that we've linked/created the user
    clearAnonUserIdCookie();
    if (refCode) {
      clearRefCodeCookie();
    }

    // Neither enrichment step gates the signup, so both report and move on.
    // They report through `reportServerError` rather than a bare console.error:
    // the Worker's captureConsoleIntegration files every console.error as its
    // own Sentry event, so interpolating `clerkUserId` into the message opened
    // a fresh issue per signup (DCM-TQ). The id belongs in a tag, leaving the
    // underlying error to group all affected signups into one issue.
    const { reportServerError } = await import("~/lib/server/report-error");

    // Sync user data from Clerk after saving
    const { syncUserFromClerk } = await import("~/lib/server/clerk-sync");
    await syncUserFromClerk(clerkUserId).catch((error: unknown) => {
      reportServerError(error, {
        area: "user_signup",
        tags: { step: "clerk_sync", clerk_user_id: clerkUserId },
      });
    });

    // Every platform account carries the DCM account tags on its GHL contact.
    const { applyUserAccountTagsInPrimaryLocation } =
      await import("~/lib/server/ghl-contact-tags");
    await applyUserAccountTagsInPrimaryLocation({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
    }).catch((error: unknown) => {
      reportServerError(error, {
        area: "user_signup",
        tags: { step: "ghl_contact_tags", clerk_user_id: clerkUserId },
      });
    });

    return {
      status: "success" as const,
      message: "User data saved successfully",
    };
  });
